import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import TrustSignals from './components/TrustSignals';
import HowItWorks from './components/HowItWorks';
import TestimonialSection from './components/TestimonialSection';
import FAQSection from './components/FAQSection';
import Image from '../../components/AppImage';
import { initFacebookPixel } from '../../utils/facebookPixel';
import { trackPageViewAPI } from '../../utils/conversionAPI';
import { getTrackingParams } from '../../utils/trackingParams';

const LandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize Facebook Pixel
    initFacebookPixel();
    
    // Capture all tracking parameters (fbclid + UTM params) from URL
    // This stores them in sessionStorage for persistence across navigation
    getTrackingParams();
    
    // Track page view with Conversion API
    trackPageViewAPI();
  }, []);

  const handleStartQuiz = () => {
    // Conversion tracking handled by GHL server-side
    // Facebook Pixel conversion events removed to prevent double counting

    navigate('/pre-assessment-preparation');
  };

  return (
    <div className="min-h-screen bg-brand-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-brand-black/95 backdrop-blur-sm border-b border-brand-slate">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-3 md:py-5">
          <div className="flex items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center flex-shrink-0">
              <Image
                src="/assets/images/FullLogo_Transparent_White-1768328997474.png"
                alt="Limitless Men logo in white on transparent background"
                className="h-10 sm:h-12 md:h-16 w-auto"
                loading="eager"
                width="200"
                height="40"
              />
            </div>
            
            <button
              onClick={handleStartQuiz}
              className="bg-brand-gold hover:bg-brand-gold/90 text-brand-black font-semibold text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-md transition-all duration-250 hover:transform hover:-translate-y-0.5 active:scale-[0.97] whitespace-nowrap"
            >
              START ASSESSMENT
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <section className="pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16">
          <HeroSection onStartQuiz={handleStartQuiz} />
        </section>

        {/* How It Works */}
        <section className="border-t border-brand-slate">
          <HowItWorks />
        </section>

        {/* Trust Signals */}
        <section className="py-12 md:py-16 lg:py-20 border-t border-brand-slate">
          <TrustSignals />
        </section>

        {/* Testimonials */}
        <section className="bg-brand-charcoal py-12 md:py-16 lg:py-20 border-t border-brand-slate">
          <TestimonialSection />
        </section>

        {/* FAQ */}
        <section className="border-t border-brand-slate">
          <FAQSection />
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 lg:py-24 border-t border-brand-slate">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
            <div className="bg-brand-charcoal rounded-lg p-10 md:p-12 lg:p-16 border border-brand-slate">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-white mb-6 uppercase tracking-tight">
                Stop Running On Outdated Software
              </h2>
              <p className="text-lg md:text-xl text-brand-steel mb-10 max-w-2xl mx-auto leading-relaxed">
                You're not broken. You're running on a survival identity installed in childhood. It's time to install a new operating system.
              </p>
              
              <button
                onClick={handleStartQuiz}
                className="bg-brand-gold hover:bg-brand-gold/90 text-brand-black font-bold text-lg md:text-xl px-10 md:px-12 py-5 md:py-6 rounded-md transition-all duration-250 hover:transform hover:-translate-y-0.5 active:scale-[0.97] uppercase tracking-wide"
              >
                Begin Assessment
              </button>
              
              <p className="text-sm md:text-base text-brand-steel mt-6">
                Free • 2 minutes • Instant results • No bullshit
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-brand-black border-t border-brand-slate py-10 md:py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            {/* Brand */}
            <div>
              <Image
                src="/assets/images/FullLogo_Transparent_White-1768328997474.png"
                alt="Limitless Men logo in white"
                className="h-10 w-auto mb-4"
                loading="lazy"
                width="200"
                height="40"
              />
              <p className="text-sm text-brand-steel leading-relaxed">
                Dissolve the invisible barriers holding you back. Build the infrastructure for lasting transformation.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-brand-white mb-4 uppercase tracking-wide text-sm">Quick Links</h3>
              <ul className="space-y-2 text-sm text-brand-steel">
                <li><a href="https://limitlessmen.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">Privacy Policy</a></li>
                <li><a href="https://limitlessmen.com/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold transition-colors">Terms of Service</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold text-brand-white mb-4 uppercase tracking-wide text-sm">Contact</h3>
              <p className="text-sm text-brand-steel">
                support@limitlessmen.com
              </p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-brand-slate text-center">
            <p className="text-sm text-brand-steel">
              © {new Date()?.getFullYear()} Limitless Men. Limitless Men is a DBA of Mind Boss LLC.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;